import { Bindable, NotifyProperty } from "bindable-data";
import { INetworkClient } from "../../../../../networking/INetworkClient";
import { IUpgradeBuildingModel } from "./IUpgradeBuildingModel";
import { UpgradeItemType } from "../types/UpgradeItemType";
import { UpgradeItemElementType } from "../types/UpgradeItemElementType";
import { BuildingUpgradeItem } from "./BuildingUpgradeItem";
import { UpgradeBuildingItemResultDto } from "../../../../../networking/data/responses/upgrade/UpgradeBuildingItemResultDto";
import { FixBuildingItemResultDto } from "../../../../../networking/data/responses/upgrade/FixBuildingItemResultDto";
import { BuildingUpgradeItemDto } from "../../../../../networking/data/responses/upgrade/BuildingUpgradeItemDto";
import { LockUpgradeItem } from "./LockUpgradeItem";
import { ItemElement } from "./ItemElement";

export class UpgradeBuildingModel extends Bindable implements IUpgradeBuildingModel {
    private readonly networkClient: INetworkClient;

    public readonly id: NotifyProperty<UpgradeItemType>;
    public readonly title: NotifyProperty<string>;
    public readonly imageUrl: NotifyProperty<string>;
    public readonly elementId: NotifyProperty<UpgradeItemElementType>;
    public readonly lock: NotifyProperty<LockUpgradeItem>;
    public readonly upgradePoints: NotifyProperty<number>;
    public readonly fixCostInCoins: NotifyProperty<number>;
    public readonly upgradeCostInCoins: NotifyProperty<number>;
    public readonly fixCostInTokens: NotifyProperty<string>;
    public readonly upgradeCostInTokens: NotifyProperty<string>;
    public readonly returnedTokensAmountForTokensFix: NotifyProperty<string>;
    public readonly returnedTokensAmountForCoinsFix: NotifyProperty<string>;
    public readonly returnedTokensAmountForTokensUpgrade: NotifyProperty<string>;
    public readonly returnedTokensAmountForCoinsUpgrade: NotifyProperty<string>;
    public readonly elements: NotifyProperty<ItemElement>;

    public constructor(
        networkClient: INetworkClient,
        upgradeItemDto: BuildingUpgradeItemDto
    ) {
        super();
        this.networkClient = networkClient;
        const upgradeItem = new BuildingUpgradeItem(upgradeItemDto);
        this.id = new NotifyProperty<UpgradeItemType>(this, "id", upgradeItem.id);
        this.title = new NotifyProperty<string>(this, "title", upgradeItem.title);
        this.imageUrl = new NotifyProperty<string>(this, "imageUrl", upgradeItem.imageUrl);
        this.elementId = new NotifyProperty<UpgradeItemElementType>(this, "levels", upgradeItem.elementId);
        this.lock = new NotifyProperty<LockUpgradeItem>(this, "levels", upgradeItem.lock);
        this.upgradePoints = new NotifyProperty<number>(this, "currentLevel", upgradeItem.upgradePoints);
        this.fixCostInCoins = new NotifyProperty<number>(this, "currentLevel", upgradeItem.fixCostInCoins);
        this.upgradeCostInCoins = new NotifyProperty<number>(this, "currentLevel", upgradeItem.upgradeCostInCoins);
        this.fixCostInTokens = new NotifyProperty<string>(this, "fixCostInTokens", upgradeItem.fixCostInTokens);
        this.upgradeCostInTokens = new NotifyProperty<string>(this, "upgradeCostInTokens", upgradeItem.upgradeCostInTokens);
        this.returnedTokensAmountForTokensFix = new NotifyProperty<string>(this, "returnedTokensAmountForTokensFix", upgradeItem.returnedTokensAmountForTokensFix);
        this.returnedTokensAmountForCoinsFix = new NotifyProperty<string>(this, "returnedTokensAmountForCoinsFix", upgradeItem.returnedTokensAmountForCoinsFix);
        this.returnedTokensAmountForTokensUpgrade = new NotifyProperty<string>(this, "returnedTokensAmountForTokensUpgrade", upgradeItem.returnedTokensAmountForTokensUpgrade);
        this.returnedTokensAmountForCoinsUpgrade = new NotifyProperty<string>(this, "returnedTokensAmountForCoinsUpgrade", upgradeItem.returnedTokensAmountForCoinsUpgrade);
        this.elements = new NotifyProperty<ItemElement>(this, "currentLevel", upgradeItem.elements);
    }

    public async upgrade(buildingId: string, perCoins: boolean): Promise<UpgradeBuildingItemResultDto> {
        const upgradeItemInputBody = perCoins ? { upgradeCostInCoins: this.upgradeCostInCoins.value } : { upgradeCostInTokens: this.upgradeCostInTokens.value }
        return await this.networkClient.upgradeBuilding(buildingId, this.id.value, upgradeItemInputBody);
    }

    public async fix(buildingId: string, perCoins: boolean): Promise<FixBuildingItemResultDto> {
        const fixItemInputBody = perCoins ? { fixCostInCoins: this.fixCostInCoins.value } : { fixCostInTokens: this.fixCostInTokens.value }
        return await this.networkClient.fixBuilding(buildingId, this.id.value, fixItemInputBody);
    }

    public updateValuesFromUpgradeOrFix(buildingUpgradeItemsDto: Array<BuildingUpgradeItemDto>): void {
        if (buildingUpgradeItemsDto == undefined || buildingUpgradeItemsDto.length == 0) {
            throw new Error(`Failed to get building upgrade items.`);
        }
        const buildingUpgradeItemDto = buildingUpgradeItemsDto.find((item) => item.id === this.id.value);
        if (buildingUpgradeItemDto == undefined) {
            throw new Error(`Upgrade item data not found with id ${this.id.value}.`);
        }
        this.updateValuesFromUpgradeItemDto(buildingUpgradeItemDto);
    }

    private updateValuesFromUpgradeItemDto(buildingUpgradeItemDto: BuildingUpgradeItemDto): void {
        const buildingUpgradeItem = new BuildingUpgradeItem(buildingUpgradeItemDto);
        this.id.setValue(buildingUpgradeItem.id);
        this.title.setValue(buildingUpgradeItem.title);
        this.imageUrl.setValue(buildingUpgradeItem.imageUrl);
        this.elementId.setValue(buildingUpgradeItem.elementId);
        this.lock.setValue(buildingUpgradeItem.lock);
        this.upgradePoints.setValue(buildingUpgradeItem.upgradePoints);
        this.fixCostInCoins.setValue(buildingUpgradeItem.fixCostInCoins);
        this.upgradeCostInCoins.setValue(buildingUpgradeItem.upgradeCostInCoins);
        this.elements.setValue(buildingUpgradeItem.elements);
        this.fixCostInTokens.setValue(buildingUpgradeItem.fixCostInTokens);
        this.upgradeCostInTokens.setValue(buildingUpgradeItem.upgradeCostInTokens);
        this.returnedTokensAmountForTokensFix.setValue(buildingUpgradeItem.returnedTokensAmountForTokensFix);
        this.returnedTokensAmountForCoinsFix.setValue(buildingUpgradeItem.returnedTokensAmountForCoinsFix);
        this.returnedTokensAmountForTokensUpgrade.setValue(buildingUpgradeItem.returnedTokensAmountForTokensUpgrade);
        this.returnedTokensAmountForCoinsUpgrade.setValue(buildingUpgradeItem.returnedTokensAmountForCoinsUpgrade);
    }
}
